// Shapes the office screens read from the action layer (customers.get, calls.get, jobs.list).

import type { AddressPointView } from '@flowology/ui-web';
import type { CampaignRef } from './settings/campaigns-model.ts';
import type { CallRecording } from './calls/transcript-model.ts';
import type { CustomerOpen, MembershipCard } from './history/bp-customers-model.ts';

export interface ContactMethod {
  id: string;
  kind: 'phone' | 'email';
  value: string;
  isPrimary: boolean;
  consent: boolean;
  marketingConsent: boolean;
  doNotContact: boolean;
  /** Who agreed to service messages on this channel, when and how (null: a customer, an import or the system). */
  consentAt?: string | null;
  consentSource?: string | null;
  consentBy?: string | null;
}

/** An address as customers.get sends it (a billing address, or a location's street). */
export interface Address {
  line1: string;
  line2: string | null;
  city: string;
  region: string | null;
  postal: string | null;
  country: string;
}

/** A piece of equipment at one of a customer's addresses (equipment.update, equipment.retire). */
export interface Equipment {
  id: string;
  locationId: string;
  kind: string;
  make: string | null;
  model: string | null;
  serial: string | null;
  size: string | null;
  installedOn: string | null;
  notes: string | null;
  retiredAt: string | null;
  retiredReason: string | null;
  rowVersion: number;
  /** Its age in whole years, from its install day (customers.get); null when that isn't known. */
  ageYears?: number | null;
}

export interface Customer {
  id: string;
  number: string;
  kind: 'residential' | 'commercial';
  displayName: string;
  status: 'active' | 'inactive';
  doNotContact: boolean;
  /** Passed back with an edit, so one made from a stale copy is refused (F-53). */
  rowVersion?: number;
  /** The office's notes on the customer (customers.update). */
  notes?: string | null;
  billingAddress?: Address | null;
  /** Days to pay and whether every job needs a PO (F-29); a location's bill-to has its own. */
  billingTerms?: { poRequired: boolean; termsDays: number | null };
  /** Charged no tax, with the exemption's reference and why (BL-094); new drafts follow it. */
  taxExempt?: { exempt: boolean; reference: string | null; reason: string | null };
  tags?: { id: string; name: string }[];
  /** Their equipment on record, in service first (customers.get, 0057). */
  equipment?: Equipment[];
  /** What's open with them, on the jobs this person sees (customers.get). */
  open?: CustomerOpen;
  /** The plan they're on now (customers.get); null when none. */
  membership?: MembershipCard | null;
  locations: {
    id: string;
    name: string | null;
    line1: string;
    line2?: string | null;
    city: string;
    region?: string | null;
    postal?: string | null;
    country?: string;
    accessNotes: string | null;
    /** Only for roles that may see them (customers.pii.view). */
    accessCodes?: string | null;
    rowVersion?: number;
    billTo?: { customerId: string; name: string; poRequired?: boolean; termsDays?: number | null } | null;
    /** Who hears about the address's visits instead of the customer (F-54); null: the customer. */
    siteContact?: SiteContact | null;
    /** People only on this address (a tenant, a caretaker), not the customer's own contacts. */
    contacts?: Contact[];
    /** Its point on the map and where it came from (customers.get, BL-018); null: none yet. */
    point?: AddressPointView | null;
  }[];
  contacts: Contact[];
}

export interface Contact {
  id: string;
  name: string;
  title: string | null;
  /** As read: sent back with a rename, so one from a stale tab is refused (QA-697). */
  rowVersion?: number;
  methods: ContactMethod[];
}

/** An address's site contact: one of the customer's, the bill-to's, or only on the address. */
export interface SiteContact extends Contact {
  of: 'customer' | 'bill_to' | 'address';
  /** Their own customer is do-not-contact (the bill-to, for a property manager's caretaker). */
  customerDoNotContact: boolean;
}

export interface Call {
  id: string;
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  status: string;
  startedAt: string;
  outcome: string | null;
  /** Why it didn't book (BL-003), a lead or not, excused or not. */
  reason: { id: string; name: string; isLead: boolean; excused: boolean } | null;
  /** Under a minute: excused whatever its reason. */
  underAMinute: boolean;
  /** How long it ran, in seconds (a voicemail's is the message's); null while it runs or unknown. */
  durationSeconds?: number | null;
  /** The job it booked: its number and its type's name. */
  job?: { number: string; type: string } | null;
  /** Who took it, once: the person whose booking rate it is. */
  takenBy: string | null;
  /** Who answered it, as the phone system names them (BL-130): ServiceTitan's agent; null when unnamed. */
  answeredBy?: string | null;
  /** An owner's or manager's review of the reason; the taker can't change it after. */
  reviewed: { at: string; changed: boolean; note: string | null } | null;
  /** A missed call or voicemail handled on the caller's later call (the callback queue). */
  handledOn: { callId: string; at: string } | null;
  /** When a missed call or voicemail was texted back (BL-061). */
  textedBackAt: string | null;
  /** The campaign it's credited to (BL-008): the number dialled's, or what the office booked it under. */
  campaign?: CampaignRef | null;
  /** The tracking number's label, when the line dialled has one. */
  trackingLabel?: string | null;
  /** calls.get only, for recordings.play: its Twilio recording and what was said (BL-083). */
  recording?: CallRecording | null;
  /** A reason suggested from the call (a transcript), waiting for the taker's yes. */
  suggestedReasonId: string | null;
  customerId: string | null;
  locationId: string | null;
  jobId: string | null;
  matches: { customerId: string; customerName: string; contactName: string }[];
  openCustomerId: string | null;
  notes: string | null;
}

export interface JobRow {
  id: string;
  number: string;
  status: string;
  kind: string;
  summary: string;
  customerName: string;
}

/** Where a booking starts: from a call, for a customer, or blank. */
export interface BookStart {
  customerId?: string;
  callId?: string;
  /** From the call (calls item 5): the address it's about, and its notes, which start the job's summary. */
  locationId?: string | null;
  notes?: string | null;
  /** Booking the work an estimate sold for later (To book): its lines go on the new job (QA-056). */
  estimate?: { id: string; number: string; jobId: string };
  /** Booking a lead (To book): jobs.book carries it, and the lead is then booked. */
  lead?: { id: string; name: string | null; phone: string | null; email: string | null; summary: string };
}

/** The phone a confirmation text would go to, or why there is none. */
export function textablePhone(
  c: Customer,
): { method: ContactMethod; firstName: string } | { reason: string } {
  if (c.doNotContact) return { reason: 'the customer is do-not-contact' };
  for (const k of c.contacts)
    for (const m of k.methods)
      if (m.kind === 'phone' && m.consent && !m.doNotContact)
        return { method: m, firstName: k.name.split(/\s+/)[0] ?? k.name };
  return { reason: 'no phone has agreed to texts' };
}

/**
 * Where a visit's texts go (F-54): the address's site contact when it has one, never the customer
 * instead (the server holds to it too), else textablePhone. With `siteContact`, the name to show.
 */
export function visitTextTo(
  c: Customer,
  locationId: string | null,
): ({ method: ContactMethod; firstName: string } | { reason: string }) & { siteContact?: string } {
  const site = c.locations.find((l) => l.id === locationId)?.siteContact;
  if (!site) return textablePhone(c);
  const said = { siteContact: site.name };
  if (c.doNotContact) return { reason: 'the customer is do-not-contact', ...said };
  if (site.customerDoNotContact)
    return { reason: `${site.name}, the site contact, is do-not-contact`, ...said };
  const phones = site.methods.filter((m) => m.kind === 'phone');
  const m = phones.find((x) => x.consent && !x.doNotContact);
  if (m) return { method: m, firstName: site.name.split(/\s+/)[0] ?? site.name, ...said };
  return {
    reason:
      phones.length && phones.every((x) => x.doNotContact)
        ? `${site.name}, the site contact, is do-not-contact`
        : `${site.name}, the site contact, hasn’t agreed to texts`,
    ...said,
  };
}

/** "Site contact: Dee Alvarez · (250) 555-0105": the name, and their first phone to call. */
export function siteContactLine(site: Contact): string {
  const p =
    site.methods.find((m) => m.kind === 'phone' && m.isPrimary) ??
    site.methods.find((m) => m.kind === 'phone');
  return `Site contact: ${site.name}${p ? ` · ${phoneText(p.value)}` : ''}`;
}

/**
 * A person only on an address (ServiceTitan's location contacts; history fix 4), as its card lists
 * them: "At this address: Jo Lee · (604) 555-0101 · jo@example.com". The address's own unnamed one
 * (the load names it "Primary") by its numbers alone.
 */
export function addressContactLine(c: Contact): string {
  const reach = c.methods.map((m) => (m.kind === 'phone' ? phoneText(m.value) : m.value));
  const who = c.name === 'Primary' ? [] : [c.name];
  return `At this address: ${[...who, ...reach].join(' · ') || c.name}`;
}

/**
 * "(604) 555-0101" for a North American number: E.164, or the bare ten digits (with or without the 1)
 * a number came over from ServiceTitan as (history fix 1); anything else as given.
 */
export function phoneText(e164: string): string {
  const m = /^(?:\+?1)?(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}
